import { useEffect, useRef } from 'react';

import { useEventCallback } from '@gitbook/util-react-hooks';

type OverlayType = 'palette' | 'spine' | 'popover' | 'feedback' | 'toast';

/**
 * Check if any overlay is open.
 * This includes:
 * - Palette overlay
 * - Spine overlay
 * - Legacy Popover overlay
 */
export function checkIsOverlayOpen(options: { includeOverlayTypes: OverlayType[] }) {
    return Boolean(
        (options.includeOverlayTypes.includes('palette') &&
            document.querySelector('[data-palette-overlay="true"]')) ||
            (options.includeOverlayTypes.includes('spine') &&
                document.querySelector('[data-spine-overlay="true"]')) ||
            (options.includeOverlayTypes.includes('popover') &&
                document.querySelector('[data-popover-overlay="true"]')) ||
            (options.includeOverlayTypes.includes('toast') &&
                document.querySelector('[data-sonner-toast]')) ||
            // Bucket feedback dialog
            (options.includeOverlayTypes.includes('feedback') &&
                document
                    .getElementById('bucket-feedback-dialog-container')
                    ?.shadowRoot?.querySelector('dialog')?.open)
    );
}

/**`
 * Check if an overlay is open and gives the opportunity to listen when it's closed.
 */
export function useCheckIsOverlayOpen(
    options: { includeOverlayTypes: OverlayType[] } = {
        includeOverlayTypes: ['palette', 'spine', 'popover', 'feedback'],
    }
) {
    const { includeOverlayTypes } = options;
    const mouseUpCleanupRef = useRef<(() => void) | null>(null);
    useEffect(() => () => mouseUpCleanupRef.current?.(), []);
    return useEventCallback((options?: { onClose?: () => void }) => {
        const onClose = options?.onClose;
        if (checkIsOverlayOpen({ includeOverlayTypes })) {
            mouseUpCleanupRef.current?.();

            // When a mouse up event is triggered, we reevaluate if the overlay is open.
            const handleMouseUp = () => {
                // Overlay is closed, we cleanup the event listener.

                if (!checkIsOverlayOpen({ includeOverlayTypes })) {
                    mouseUpCleanupRef.current?.();
                    onClose?.();
                }
            };
            document.body.addEventListener('mouseup', handleMouseUp);
            mouseUpCleanupRef.current = () => {
                document.body.removeEventListener('mouseup', handleMouseUp);
                mouseUpCleanupRef.current = null;
            };
            return true;
        }
        return false;
    });
}
